import type {
  AddRiskToEnterpriseRiskInput,
  AddRiskToEnterpriseRiskMutation,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTRPC } from '@/utils/trpc';

// Maps the tRPC `{ affectedRows }` response to the GraphQL mutation shape so the
// flag-gated wrapper can return one type regardless of which path ran.
const mapTrpcResponseToGraphQL = (
  affectedRows: number
): AddRiskToEnterpriseRiskMutation => ({
  addRiskToEnterpriseRisk: {
    __typename: 'GenericMutationOutput',
    affected_rows: affectedRows,
  },
});

/**
 * tRPC implementation of the add-risk-to-enterprise-risk mutation (COI-432).
 * NOT imported outside this directory — consumers use the flag-gated
 * `useAddRiskToEnterpriseRisk` wrapper.
 *
 * Cache-invalidation parity with the Apollo twin's `evictField` set
 * (`risk` / `risk_score` / `enterprise_risk` / `enterprise_risk_score`): the
 * enterprise-risk register/detail/tier scores change, and the linked risks'
 * enterprise-risk attribution changes.
 */
export const useAddRiskToEnterpriseRiskTRPC = () => {
  const trpc = useTRPC();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    ...trpc.frontend.enterpriseRisk.link.mutationOptions({
      onSuccess: async () => {
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.register.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.enterpriseRisk.getByTier.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.riskById.queryKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.frontend.risk.register.queryKey(),
          }),
        ]);
      },
    }),
  });

  return {
    addRiskToEnterpriseRisk: async (
      objects: AddRiskToEnterpriseRiskInput[]
    ): Promise<AddRiskToEnterpriseRiskMutation> => {
      const result = await mutation.mutateAsync({ objects });

      return mapTrpcResponseToGraphQL(result.affectedRows);
    },
    loading: mutation.isPending,
    error: mutation.error,
  };
};
